import type { WireMessage } from "./agentMemory";
import { keepsWholeResult, resultMaxCharsFor } from "./tools";
import type { TurnEnd } from "./turnLoop";

export const buildAnswerUserMessage = (p: {
  question: string;
  toolContext: string[];
  plannerReply: string | null;
  endedBy?: UnfinishedEnd;
  earlier?: readonly WireMessage[];
}): string => {
  const earlier = earlierExchange(p.earlier ?? []);
  const sections = [
    p.toolContext.length ? `Data hentet fra systemet:\n${boundedToolContext(p.toolContext)}` : null,
    p.plannerReply !== null ? `Assistentens svar til brukeren:\n${p.plannerReply}` : null,
    p.endedBy ? UNFINISHED[p.endedBy] : null,
  ].filter((section): section is string => section !== null);
  return [
    ...(earlier ? [earlier] : []),
    `Spørsmål: ${p.question}`,
    ...(sections.length ? sections : ["(ingen data)"]),
  ].join("\n\n");
};

export const EARLIER_EXCHANGE_MESSAGES = 6;
export const EARLIER_MESSAGE_CHARS = 1_500;

const earlierExchange = (messages: readonly WireMessage[]): string | null => {
  const said = messages
    .filter((m) => (m.role === "user" || m.role === "assistant") && typeof m.content === "string" && m.content.trim())
    .slice(-EARLIER_EXCHANGE_MESSAGES)
    .map((m) => {
      const text = (m.content as string).trim();
      const bounded = text.length <= EARLIER_MESSAGE_CHARS ? text : `${text.slice(0, EARLIER_MESSAGE_CHARS)}… [avkortet]`;
      return `${m.role === "user" ? "Bruker" : "Assistent"}: ${bounded}`;
    });
  if (!said.length) return null;
  return (
    "Samtalen før spørsmålet (bakgrunn). Er spørsmålet en fortsettelse som «fortsett» eller «ja», gjelder det " +
    "oppgaven brukeren ga her: si hva som nå er gjort av den, hva som gjenstår av den, eller at den er ferdig, og " +
    `tilby å gå videre med neste del av den.\n${said.join("\n")}`
  );
};

export type UnfinishedEnd = Exclude<TurnEnd, "stopped">;

const UNFINISHED: Record<UnfinishedEnd, string> = {
  sizeLimit:
    "Oppgaven er IKKE ferdig: denne meldingen ble for stor til at assistenten kunne fortsette. Si kort hva som er " +
    "gjort og lagret ut fra dataene over, hva som gjenstår, og at brukeren kan skrive «fortsett» for å gå videre.",
  stepLimit:
    "Oppgaven er IKKE ferdig: assistenten brukte opp stegene den har per melding. Si kort hva som er gjort og " +
    "lagret ut fra dataene over, hva som gjenstår, og at brukeren kan skrive «fortsett» for å gå videre.",
};

export const ANSWER_TOOL_CONTEXT_CHARS = 150_000;

export const boundedToolContext = (entries: readonly string[]): string => {
  const capped = entries.map((entry) => {
    const toolName = entry.slice(0, entry.indexOf(":"));
    const maxChars = resultMaxCharsFor(toolName);
    return entry.length <= maxChars || keepsWholeResult(toolName) ? entry : `${entry.slice(0, maxChars)}… [avkortet]`;
  });
  const kept: string[] = [];
  let size = 0;
  for (let i = capped.length - 1; i >= 0 && size + capped[i].length <= ANSWER_TOOL_CONTEXT_CHARS; i--) {
    kept.unshift(capped[i]);
    size += capped[i].length;
  }
  const left = capped.length - kept.length;
  const note = left > 0 ? [`(${left} tidligere resultater fra denne meldingen er utelatt her.)`] : [];
  return [...note, ...kept].join("\n\n");
};
